import { Vector3 } from 'three';

import { elementFactory } from '@/modules/canvas/factories';
import { FDDM_TAG, INTERACTIVITY_OFF } from '@/modules/floorplanDesign/constants';
import { METERS_TO_MILLIMETERS_FACTOR } from '@/modules/canvas/factories/element';

import {
  ORIGIN_GROUP_ID,
  ORIGIN_HEX_COLOR,
  LINE_STROKE_WIDTH,
  OriginElementSizing,
} from '../constants';

export const buildOriginElements = (scale: number, interactive: boolean = false) => {
  const originPoint = new Vector3(0, 0, 0);
  const yVectorPoint = new Vector3(0, OriginElementSizing.AXIS_LINE_LENGTH * scale, 0);
  const xVectorPoint = new Vector3(OriginElementSizing.AXIS_LINE_LENGTH * scale, 0, 0);
  const interactivity = {
    ...INTERACTIVITY_OFF,
    clickable: interactive,
    hoverable: interactive,
    selectable: interactive,
    draggable: interactive,
  };

  const originGroupElement = elementFactory.buildGroupElement({
    id: ORIGIN_GROUP_ID,
    name: ORIGIN_GROUP_ID,
    position: originPoint,
    interactivity,
    tags: [FDDM_TAG.ORIGIN],
  });

  const axisLineX = elementFactory.buildLineElement({
    id: 'x-axis',
    name: 'x-axis',
    pointA: originPoint,
    pointB: xVectorPoint,
    parentId: originGroupElement.id,
    interactivity,
    stroke: {
      color: ORIGIN_HEX_COLOR,
      width: LINE_STROKE_WIDTH,
    },
    tags: [FDDM_TAG.ORIGIN],
  });

  const axisLineY = elementFactory.buildLineElement({
    id: 'y-axis',
    name: 'y-axis',
    pointA: originPoint,
    pointB: yVectorPoint,
    parentId: originGroupElement.id,
    interactivity,
    stroke: {
      color: ORIGIN_HEX_COLOR,
      width: LINE_STROKE_WIDTH,
    },
    tags: [FDDM_TAG.ORIGIN],
  });

  const lineXArrowLegA = elementFactory.buildLineElement({
    id: 'x-axis-arrow-leg-a',
    name: 'x-axis-arrow-leg-a',
    pointA: xVectorPoint,
    pointB: xVectorPoint
      .clone()
      .add(
        new Vector3(
          -OriginElementSizing.ARROW_LEG_LENGTH,
          OriginElementSizing.ARROW_LEG_LENGTH,
          0,
        ).multiplyScalar(scale),
      ),
    parentId: originGroupElement.id,
    interactivity,
    stroke: {
      color: ORIGIN_HEX_COLOR,
      width: LINE_STROKE_WIDTH,
    },
    tags: [FDDM_TAG.ORIGIN],
  });

  const lineXArrowLegB = elementFactory.buildLineElement({
    id: 'x-axis-arrow-leg-b',
    name: 'x-axis-arrow-leg-b',
    pointA: xVectorPoint,
    pointB: xVectorPoint
      .clone()
      .add(
        new Vector3(
          -OriginElementSizing.ARROW_LEG_LENGTH,
          -OriginElementSizing.ARROW_LEG_LENGTH,
          0,
        ).multiplyScalar(scale),
      ),
    parentId: originGroupElement.id,
    interactivity,
    stroke: {
      color: ORIGIN_HEX_COLOR,
      width: LINE_STROKE_WIDTH,
    },
    tags: [FDDM_TAG.ORIGIN],
  });

  const lineYArrowLegA = elementFactory.buildLineElement({
    id: 'y-axis-arrow-leg-a',
    name: 'y-axis-arrow-leg-a',
    pointA: yVectorPoint,
    pointB: yVectorPoint
      .clone()
      .add(
        new Vector3(
          -OriginElementSizing.ARROW_LEG_LENGTH,
          -OriginElementSizing.ARROW_LEG_LENGTH,
          0,
        ).multiplyScalar(scale),
      ),
    parentId: originGroupElement.id,
    interactivity,
    stroke: {
      color: ORIGIN_HEX_COLOR,
      width: LINE_STROKE_WIDTH,
    },
    tags: [FDDM_TAG.ORIGIN],
  });

  const lineYArrowLegB = elementFactory.buildLineElement({
    id: 'y-axis-arrow-leg-b',
    name: 'y-axis-arrow-leg-b',
    pointA: yVectorPoint,
    pointB: yVectorPoint
      .clone()
      .add(
        new Vector3(
          OriginElementSizing.ARROW_LEG_LENGTH,
          -OriginElementSizing.ARROW_LEG_LENGTH,
          0,
        ).multiplyScalar(scale),
      ),
    parentId: originGroupElement.id,
    interactivity,
    stroke: {
      color: ORIGIN_HEX_COLOR,
      width: LINE_STROKE_WIDTH,
    },
    tags: [FDDM_TAG.ORIGIN],
  });

  const originCircle = elementFactory.buildCircleElement({
    id: 'origin-point',
    radius: (OriginElementSizing.CIRCLE_RADIUS / METERS_TO_MILLIMETERS_FACTOR) * scale,
    parentId: originGroupElement.id,
    position: originPoint,
    color: ORIGIN_HEX_COLOR,
    interactivity,
    tags: [FDDM_TAG.ORIGIN],
  });

  return [
    originGroupElement,
    originCircle,
    axisLineX,
    axisLineY,
    lineXArrowLegA,
    lineXArrowLegB,
    lineYArrowLegA,
    lineYArrowLegB,
  ];
};
